import React, { useEffect, useMemo, useState } from 'react';

const FIELD_ORDER = ['name', 'company', 'jobTitle', 'phone', 'website', 'address', 'notes'];
const DETERMINISTIC_FIELDS = new Set(['email', 'phone', 'website', 'linkedin', 'twitter']);
const SWAPPY_FIELDS = new Set(['name', 'company', 'jobTitle']);

function normalize(v) {
  return String(v || '').trim();
}

function displayLabelForKey(key) {
  if (key === 'jobTitle') return 'Job title';
  if (key === 'phone') return 'Phone';
  if (key === 'website') return 'Website';
  if (key === 'address') return 'Address';
  if (key === 'notes') return 'Notes';
  if (key === 'company') return 'Company';
  if (key === 'name') return 'Name';
  return key;
}

function computeDiff(existingFields, newFields) {
  const diffs = [];
  for (const key of FIELD_ORDER) {
    const oldVal = normalize(existingFields?.[key]);
    const newVal = normalize(newFields?.[key]);
    if (!newVal) continue;
    if (oldVal === newVal) continue;
    diffs.push({ key, oldVal, newVal });
  }
  return diffs;
}

export default function ArchiveDuplicateEmailModal({
  isOpen,
  existingCard,
  newFields,
  newTheme,
  fieldConfidence,
  onCancel,
  onViewExisting,
  onConfirmUpdate
}) {
  const diffs = useMemo(() => computeDiff(existingCard?.fields, newFields), [existingCard, newFields]);

  const defaultSelected = useMemo(() => {
    const selected = {};
    for (const d of diffs) {
      const conf = typeof fieldConfidence?.[d.key] === 'number' ? fieldConfidence[d.key] : null;
      // Default selection policy:
      // - High (>=0.85): checked
      // - Medium (>=0.65): checked for deterministic fields; unchecked for name/company/title unless high
      // - Low: unchecked
      if (conf == null) {
        selected[d.key] = true;
      } else if (conf >= 0.85) {
        selected[d.key] = true;
      } else if (conf >= 0.65) {
        selected[d.key] = DETERMINISTIC_FIELDS.has(d.key) ? true : !SWAPPY_FIELDS.has(d.key);
      } else {
        selected[d.key] = false;
      }
    }
    return selected;
  }, [diffs, fieldConfidence]);

  const [selected, setSelected] = useState(defaultSelected);
  const [applyTheme, setApplyTheme] = useState(!!newTheme?.primaryBackground);

  // When diffs change (new scan), re-seed selections.
  useEffect(() => {
    setSelected(defaultSelected);
    setApplyTheme(!!newTheme?.primaryBackground);
  }, [defaultSelected, newTheme]);

  const hasAnySelection = Object.values(selected).some(Boolean) || applyTheme;

  if (!isOpen) return null;

  return (
    <div className="archive-modal-overlay" onClick={onCancel}>
      <div className="archive-modal archive-edit-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Duplicate email found</h3>
        <p style={{ marginTop: 6, opacity: 0.85 }}>
          This email already exists in your Archive. Update the existing card instead of creating a new one?
        </p>

        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 6 }}>
            Existing card
          </div>
          <div style={{ padding: 10, borderRadius: 12, border: '1px solid rgba(0,0,0,0.1)' }}>
            <div style={{ fontWeight: 700 }}>{existingCard?.fields?.name || '—'}</div>
            <div style={{ opacity: 0.85, marginTop: 3 }}>
              {[existingCard?.fields?.company, existingCard?.fields?.jobTitle].filter(Boolean).join(' • ') || '—'}
            </div>
            <div style={{ opacity: 0.8, marginTop: 6, fontSize: 13 }}>
              {existingCard?.fields?.email || '—'}
            </div>
          </div>
        </div>

        <div className="archive-scan-lines" style={{ borderTop: 'none', paddingTop: 0 }}>
          <div className="archive-scan-lines-header" style={{ marginTop: 14 }}>
            <div>
              <strong>Proposed updates</strong>
              <span className="archive-scan-lines-hint"> Choose what to apply</span>
            </div>
          </div>

          {diffs.length === 0 ? (
            <div style={{ opacity: 0.85, fontSize: 13 }}>
              No new field values detected (but you can still update the theme).
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {diffs.map((d) => (
                <label key={d.key} style={{ display: 'grid', gap: 6, padding: 10, borderRadius: 12, border: '1px solid rgba(0,0,0,0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ fontWeight: 700 }}>{displayLabelForKey(d.key)}</div>
                    <input
                      type="checkbox"
                      checked={!!selected[d.key]}
                      onChange={(e) => setSelected((s) => ({ ...s, [d.key]: e.target.checked }))}
                    />
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.8 }}>
                    <div><strong>Existing:</strong> {d.oldVal || '—'}</div>
                    <div><strong>New:</strong> {d.newVal}</div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {newTheme?.primaryBackground && (
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 12, padding: 10, borderRadius: 12, border: '1px solid rgba(0,0,0,0.1)' }}>
              <div>
                <div style={{ fontWeight: 700 }}>Theme</div>
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  Apply newly scanned theme colours (background + readable text).
                </div>
              </div>
              <input
                type="checkbox"
                checked={applyTheme}
                onChange={(e) => setApplyTheme(e.target.checked)}
              />
            </label>
          )}
        </div>

        <div className="archive-modal-buttons">
          <button
            className="archive-modal-button-primary"
            type="button"
            disabled={!hasAnySelection}
            onClick={() => {
              const fieldsPatch = {};
              for (const d of diffs) {
                if (selected[d.key]) fieldsPatch[d.key] = d.newVal;
              }
              onConfirmUpdate?.({
                fieldsPatch,
                applyTheme
              });
            }}
          >
            Update existing card
          </button>
          <button
            className="archive-modal-button-secondary"
            type="button"
            onClick={() => onViewExisting?.(existingCard)}
          >
            View existing
          </button>
          <button className="archive-modal-button-cancel" type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

